import { QuestionService } from '../services/question.service';

/**
 * Handles saving a new question
 * @param noteIndex The index of the note in the content data
 * @param questionIndex The index of the question in the note's questions
 * @param contentData The current content data
 * @param setItemSubmitting State setter for item submitting status
 * @param setContentData State setter for content data
 * @param setEditingQuestionIndices State setter for editing question indices
 */
export const saveNewQuestion = async (
  noteIndex: number,
  questionIndex: number,
  contentData: any,
  setItemSubmitting: React.Dispatch<React.SetStateAction<{type: string, index: number, subIndex?: number} | null>>,
  setContentData: React.Dispatch<React.SetStateAction<any>>,
  setEditingQuestionIndices: React.Dispatch<React.SetStateAction<{noteIndex: number, questionIndex: number} | null>>
): Promise<void> => {
  if (!contentData) return;
  
  const note = contentData.notes[noteIndex];
  
  if (!note.questions) {
    return;
  }
  
  const question = note.questions[questionIndex];
  
  if (!note.id) {
    alert('You must save the note before adding questions to it');
    return;
  }
  
  try {
    setItemSubmitting({ type: 'question', index: noteIndex, subIndex: questionIndex });
    
    const newQuestion = await QuestionService.createQuestion({
      question: question.question.trim(),
      answer: question.answer.trim(),
      note_id: note.id
    });
    
    // Update content data
    const updatedNotes = [...contentData.notes];
    const updatedNote = updatedNotes[noteIndex];
    
    if (updatedNote.questions) {
      updatedNote.questions[questionIndex] = newQuestion;
      
      setContentData({
        ...contentData,
        notes: updatedNotes
      });
    }
    
    setEditingQuestionIndices(null);
  } catch (err) {
    console.error('Error creating question:', err);
    alert('Failed to create question. Please try again later.');
  } finally {
    setItemSubmitting(null);
  }
};

/**
 * Handles updating an existing question
 * @param noteIndex The index of the note in the content data
 * @param questionIndex The index of the question in the note's questions
 * @param contentData The current content data
 * @param isNewContent Flag indicating if this is new content
 * @param setItemSubmitting State setter for item submitting status
 * @param setContentData State setter for content data
 * @param setEditingQuestionIndices State setter for editing question indices
 * @param saveNewQuestionFn Function to save a new question
 */
export const updateExistingQuestion = async (
  noteIndex: number,
  questionIndex: number,
  contentData: any,
  isNewContent: boolean,
  setItemSubmitting: React.Dispatch<React.SetStateAction<{type: string, index: number, subIndex?: number} | null>>,
  setContentData: React.Dispatch<React.SetStateAction<any>>,
  setEditingQuestionIndices: React.Dispatch<React.SetStateAction<{noteIndex: number, questionIndex: number} | null>>,
  saveNewQuestionFn: (noteIndex: number, questionIndex: number) => Promise<void>
): Promise<void> => {
  if (!contentData) return;
  
  const note = contentData.notes[noteIndex];
  
  if (!note.questions) {
    return;
  }
  
  const question = note.questions[questionIndex];
  
  if (!question.question || !question.answer || question.question.trim() === '' || question.answer.trim() === '') {
    alert('Question and answer cannot be empty');
    return;
  }
  
  if (isNewContent) {
    // For new content, just update local state
    setEditingQuestionIndices(null);
  } else {
    // For existing question, update in database
    if (!question.id) {
      // New question needs to be created
      await saveNewQuestionFn(noteIndex, questionIndex);
    } else {
      // Existing question needs to be updated
      try {
        setItemSubmitting({ type: 'question', index: noteIndex, subIndex: questionIndex });
        
        const updatedQuestion = await QuestionService.updateQuestion(question.id, {
          question: question.question.trim(),
          answer: question.answer.trim()
        });
        
        // Update content data
        const updatedNotes = [...contentData.notes];
        const note = updatedNotes[noteIndex];
        
        if (note.questions) {
          note.questions[questionIndex] = {
            ...note.questions[questionIndex],
            ...updatedQuestion
          };
          
          setContentData({
            ...contentData,
            notes: updatedNotes
          });
        }
        
        setEditingQuestionIndices(null);
      } catch (err) {
        console.error('Error updating question:', err);
        alert('Failed to update question. Please try again later.');
      } finally {
        setItemSubmitting(null);
      }
    }
  }
};

/**
 * Handles deleting a question
 * @param noteIndex The index of the note in the content data
 * @param questionIndex The index of the question in the note's questions
 * @param contentData The current content data
 * @param isNewContent Flag indicating if this is new content
 * @param setItemSubmitting State setter for item submitting status
 * @param setContentData State setter for content data
 */
export const deleteQuestion = async (
  noteIndex: number,
  questionIndex: number,
  contentData: any,
  isNewContent: boolean,
  setItemSubmitting: React.Dispatch<React.SetStateAction<{type: string, index: number, subIndex?: number} | null>>,
  setContentData: React.Dispatch<React.SetStateAction<any>>
): Promise<void> => {
  if (!contentData) return;
  
  const note = contentData.notes[noteIndex];
  
  if (!note.questions) {
    return;
  }
  
  const question = note.questions[questionIndex];
  
  // For new content (JSON processing), just update local state without confirmation
  if (isNewContent) {
    const updatedNotes = [...contentData.notes];
    const note = updatedNotes[noteIndex];
    
    if (note.questions) {
      note.questions.splice(questionIndex, 1);
      
      setContentData({
        ...contentData,
        notes: updatedNotes
      });
    }
    return;
  }
  
  // For existing question, confirm before deleting from database
  if (window.confirm('Are you sure you want to delete this question?')) {
    if (question.id) {
      try {
        setItemSubmitting({ type: 'question', index: noteIndex, subIndex: questionIndex });
        
        await QuestionService.deleteQuestion(question.id);
        
        // Update content data
        const updatedNotes = [...contentData.notes];
        const note = updatedNotes[noteIndex];
        
        if (note.questions) {
          note.questions.splice(questionIndex, 1);
          
          setContentData({
            ...contentData,
            notes: updatedNotes
          });
        }
      } catch (err) {
        console.error('Error deleting question:', err);
        alert('Failed to delete question. Please try again later.');
      } finally {
        setItemSubmitting(null);
      }
    } else {
      // New question that hasn't been saved yet - no need for confirmation
      const updatedNotes = [...contentData.notes];
      const note = updatedNotes[noteIndex];
      
      if (note.questions) {
        note.questions.splice(questionIndex, 1);
        
        setContentData({
          ...contentData,
          notes: updatedNotes
        });
      }
    }
  }
};